'use client';

import { Download, AlertTriangle, AlertCircle, Info, ArrowUpRight } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type { DCMAScoreResult } from '@/lib/dcma-scoring-engine';
import { generateExecutiveSummary } from '@/lib/executive-summary';
import { PlaneraLogo } from '@/lib/logo';
import { generateDCMAReport } from '@/lib/pdf-report';

import { CriterionResultsTable } from './criterion-results-table';
import type { Lead } from './lead-capture-form';
import { ProjectSummaryCard } from './project-summary-card';
import { ScoreOverview } from './score-overview';

interface FullReportProps {
  result: DCMAScoreResult;
  lead: Lead;
}

function SeverityIcon({ severity }: { severity: string }) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />;
    case 'warning':
      return <AlertCircle className="h-5 w-5 text-warning shrink-0" />;
    default:
      return <Info className="h-5 w-5 text-primary shrink-0" />;
  }
}

export function FullReport({ result, lead }: FullReportProps) {
  const executiveSummary = useMemo(() => generateExecutiveSummary(result), [result]);
  const generatedAt = useMemo(() => new Date(), []);
  const leadName = `${lead.firstName} ${lead.lastName}`.trim();

  const handleDownloadPDF = () => {
    const pdfBytes = generateDCMAReport(result, lead, executiveSummary);
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DCMA-Report-${result.projectName.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <header className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-3">
          <a
            href="https://planera.io"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity w-fit"
          >
            <PlaneraLogo className="h-7 w-auto" />
          </a>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground text-balance">
              DCMA Schedule Quality Report
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Prepared for {leadName}
              {lead.company ? ` at ${lead.company}` : ''} |{' '}
              {generatedAt.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <Button onClick={handleDownloadPDF} className="gap-2 shrink-0">
          <Download className="h-4 w-4" />
          Download PDF
        </Button>
      </header>

      {/* Score overview: overall score, grade, deductions by category, dates */}
      <ScoreOverview result={result} />

      {/* Schedule summary: activity/relationship counts + status breakdown */}
      <ProjectSummaryCard summary={result.summary} />

      <Separator />

      {/* Executive Summary */}
      <section>
        <h2 className="text-lg font-bold text-foreground mb-4">Executive Summary</h2>
        <Card className="border-primary/10 bg-primary/[0.02]">
          <CardContent className="py-6 space-y-4">
            <p className="text-sm text-foreground leading-relaxed">{executiveSummary.overallAssessment}</p>
          </CardContent>
        </Card>
      </section>

      {/* Detailed Findings */}
      {executiveSummary.sections.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-foreground mb-4">Key Findings</h2>
          <div className="space-y-4">
            {executiveSummary.sections.map((section, i) => {
              const borderColor =
                section.severity === 'critical'
                  ? 'border-l-destructive'
                  : section.severity === 'warning'
                    ? 'border-l-warning'
                    : 'border-l-primary';
              return (
                <Card key={i} className={`border-l-4 ${borderColor}`}>
                  <CardContent className="py-5">
                    <div className="flex items-start gap-3 mb-3">
                      <SeverityIcon severity={section.severity} />
                      <h3 className="text-sm font-semibold text-foreground">{section.heading}</h3>
                    </div>
                    <div className="space-y-3 pl-8">
                      {section.paragraphs.map((p, j) => (
                        <p key={j} className="text-sm text-muted-foreground leading-relaxed">
                          {p}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Recommendations */}
      {executiveSummary.recommendations.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-foreground mb-4">Recommendations</h2>
          <Card>
            <CardContent className="py-5">
              <ol className="space-y-3">
                {executiveSummary.recommendations.map((rec, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {i + 1}
                    </span>
                    <span className="text-muted-foreground leading-relaxed pt-0.5">{rec}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </section>
      )}

      <Separator />

      {/* Criterion-by-criterion breakdown with flagged items */}
      <CriterionResultsTable results={result.criterionResults} />

      {/* Closing */}
      <Card className="border-primary/10 bg-primary/[0.02]">
        <CardContent className="py-6">
          <p className="text-sm text-foreground leading-relaxed">{executiveSummary.closingStatement}</p>
        </CardContent>
      </Card>

      {/* CTA Footer */}
      <Card className="border-primary/20 bg-primary/[0.03]">
        <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
          <h3 className="text-base font-semibold text-foreground">Want to improve your schedule quality score?</h3>
          <p className="max-w-md text-sm text-muted-foreground">
            Planera helps construction teams continuously monitor, analyze, and improve schedule quality. Get automated
            DCMA checks on every schedule update.
          </p>
          <div className="flex gap-3">
            <Button onClick={handleDownloadPDF} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
            <Button asChild className="gap-2">
              <a href="https://planera.io" target="_blank" rel="noopener noreferrer">
                Learn More
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
